import React, { useState, useEffect, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import {
    handleGetUserData,
    getModalSwitch,
    handleExpireUserPassword,
    handleRemoveRoleMembership,
    handleRemoveGroupMembership,
    handleRevokePush,
    handleRevokeU2f,
    handleRevokeOtp,
    handleDeleteUserAttribute,
    setExceptionHandler,
    addDetailsBreadcrumb,
    removeDetailsBreadcrumb,
    handleAddGroupsMembership,
    removeExpireUserPasswordResponseAction
} from '@app/store/actions';
import {
    IUserDetail,
    IAlternatePrincipalName,
    IRole,
    IGroup,
    IUserAttribute,
    TranslationsTypes,
    IGlobalState,
    IShellConfig,
    IGroupsDialogData,
    IAgentDetail,
    IAgentClaims
} from '@app/models';
import PersonIcon from '@material-ui/icons/Person';
import EmailIcon from '@material-ui/icons/Email';
import {
    DATE_FORMAT,
    passlyFormatDate,
    DATE_TIME_FORMAT,
    emptyGuid,
    defaultOrgId,
    u2fEnabledAttributeName,
    otpEnabledAttributeName,
    createTranslation,
    genericErrorToast,
    handleModalSuccess,
    interpolateTranslation,
    hasOrgPermission,
    userAttributesToHide,
    isDefaultOrgUser,
    hasDefaultOrgAdminPermission,
    isLoggedInToDefaultOrg
} from '@app/helpers';
import { ManageAccount, Administrator } from '@app/store/constants';
import { toast } from 'react-toastify';
import {
    ModalComponent,
    numberOfDevices,
    TrustedDevices,
    DarkWebCompromises,
    IModalData,
    PageLoader,
    PasslyButton,
    Infobox,
    AvatarComponent
} from '@app/components';
import ResetPasswordDialog from '@app/pages/directoryManager/dialogs/resetPassword.dialog';
import { AddRolesDialog } from '@app/pages/directoryManager/dialogs/addRoles.dialog';
import AddEditUserAttributesDialog from '@app/pages/directoryManager/dialogs/addEditUserAttributes';
import { confirm } from '@app/components/Modal/confirmModal';
import { replaceDynamicTranslation } from '@app/helpers/strings';
import EditUserDialog from '@app/pages/directoryManager/dialogs/editUser.dialog';
import { FormMode } from '@app/helpers/enums';
import { usersService, basicService, agentsService } from '@app/services';
import { JitSection } from './components/jitSection.component';
import { JitToggle } from './components/jitToggle.component';
import { useParams } from 'react-router';
import { AddGroups } from '../dialogs';
import { InfoOutlined } from '@material-ui/icons';
import { Link } from '@material-ui/core';
import { AxiosError } from 'axios';
import EditCustomClaimsDialog from '@app/pages/directoryManager/dialogs/editCustomClaims.dialog';
import CustomClaimsConfig from '@app/pages/directoryManager/components/customClaimsConfig.component';
import { PasslyTooltip } from '@app/componentsV2';
import { handleSetSnackbar } from '@app/store/actions/snackbar.actions';

const UserDetailsPage = () => {
    let thisUser: IUserDetail = useSelector((state: IGlobalState) => state.users.user);
    const shellConfig: IShellConfig = useSelector((state: IGlobalState) => state.basic.shellConfig);
    const loadingData: boolean = useSelector((state: IGlobalState) => state.basic.loadingData);
    const expireUserPasswordResponse: any = useSelector((state: IGlobalState) => state.users.expireUserPasswordResponse);
    const removeRoleMembershipResponse: any = useSelector((state: IGlobalState) => state.users.removeRoleMembershipResponse);
    const removeGroupMembershipResponse: any = useSelector((state: IGlobalState) => state.users.removeGroupMembershipResponse);
    const modalSwitchState: boolean = useSelector((state: any) => state.modal);

    const [userPasswordPolicy, setUserPasswordPolicy] = useState<any>({});
    const [removeRoleId, setRemoveRoleId] = useState<string>('');
    const [removeGroupId, setRemoveGroupId] = useState<string>('');
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });
    const [triggerJit, setTriggerJit] = useState<boolean>(false);
    const [jitDisabled, isJitDisabled] = useState<boolean>(thisUser.SupportsImpersonation);
    const [dirSync, isDirSync] = useState<boolean>(false);
    const [office365, isOffice365] = useState<boolean>(false);
    const [inOnboardState, setInOnboardState] = useState<boolean>(false);
    const [agentClaims, setAgentClaims] = useState<IAgentClaims | undefined>(undefined);
    const [agents, setAgents] = useState<IAgentDetail[]>([]);
    const [selectedAgentValue, setSelectedAgentValue] = useState(agents[0] ? agents[0].ClientId : '');
    const agent = agents.find((agent) => agent.ClientId === selectedAgentValue);
    const [isNeedToUpdateAgentClaims, setIsNeedToUpdateAgentClaims] = useState(false);

    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const { id: userId } = useParams<{ id: string }>();

    const showDarkWeb =
        shellConfig.intercom.dwidEnabled &&
        shellConfig.intercom.salesforceId &&
        shellConfig.organizations.find((o) => o.id === thisUser.OrganizationId)?.hasDarkWebChannelId;

    useEffect(() => {
        dispatch(handleGetUserData(userId));
        dispatch(setExceptionHandler(true));
        usersService.getUserPassPolicy(userId).then((response: any) => {
            setUserPasswordPolicy(response.data);
        });

        if (hasOrgPermission([ManageAccount])) {
            usersService.onboardingState(userId, genericErrorToast, handleSuccessOnboardState);
        }

        agentsService.getOpenIdAgentProfiles().then(({ data }) => {
            setAgents(data.value);
            setSelectedAgentValue(data.value[0]?.ClientId);
        });

        return () => {
            dispatch(removeDetailsBreadcrumb());
        };
    }, []);

    useEffect(() => {
        if (removeRoleMembershipResponse && removeRoleMembershipResponse.status >= 200 && removeRoleMembershipResponse.status < 300) {
            thisUser.MemberOf = thisUser.MemberOf.filter((item) => item.Id !== removeRoleId);
        } else if (removeRoleMembershipResponse && removeRoleMembershipResponse.response && removeRoleMembershipResponse.response.status === 400) {
            toast(removeRoleMembershipResponse.response.data.error.message);
        }

        setRemoveRoleId('');
    }, [removeRoleMembershipResponse]);

    useEffect(() => {
        if (removeGroupMembershipResponse && removeGroupMembershipResponse.status >= 200 && removeGroupMembershipResponse.status < 300) {
            thisUser.Groups = thisUser.Groups.filter((item) => item.Id !== removeGroupId);
        } else if (removeGroupMembershipResponse && removeGroupMembershipResponse.response && removeGroupMembershipResponse.response.status === 400) {
            toast(removeGroupMembershipResponse.response.data.error.message);
        }

        setRemoveGroupId('');
    }, [removeGroupMembershipResponse]);

    useEffect(() => {
        dispatch(addDetailsBreadcrumb(thisUser.DisplayName));
    }, [thisUser]);

    useEffect(() => {
        if (!!thisUser.SyncSource) {
            if (thisUser.SyncSource && thisUser.SyncSource === emptyGuid) {
                isDirSync(false);
            } else {
                isDirSync(true);
            }
        }
    }, [thisUser.SyncSource]);

    useEffect(() => {
        if (!!thisUser.ApplicationSyncSource) {
            if (thisUser.ApplicationSyncSource && thisUser.ApplicationSyncSource === emptyGuid) {
                isOffice365(false);
            } else {
                isOffice365(true);
            }
        }
    }, [thisUser.ApplicationSyncSource]);

    useEffect(() => {
        if (expireUserPasswordResponse && expireUserPasswordResponse.status === 204) {
            dispatch(removeExpireUserPasswordResponseAction());
            toast(translation('ONBOARD_SETPASSWORD_EXPIRED'));
        } else if (expireUserPasswordResponse && expireUserPasswordResponse.response && expireUserPasswordResponse.response.status === 400) {
            dispatch(removeExpireUserPasswordResponseAction());
            toast(expireUserPasswordResponse.response.data.error.message);
        }
    }, [expireUserPasswordResponse]);

    useEffect(() => {
        return () => {
            setTriggerJit(false);
        };
    }, [triggerJit]);

    const handleGetUserCustomClaimsSuccess = (response: any) => {
        setAgentClaims(response.data);
    };
    const handleGetUserCustomClaimsError = () => {
        setAgentClaims(undefined);
    };

    useEffect(() => {
        if (selectedAgentValue && agent) {
            usersService.getUserCustomClaims(userId, agent.Id, handleGetUserCustomClaimsSuccess, handleGetUserCustomClaimsError);
        }

        if (isNeedToUpdateAgentClaims) {
            setIsNeedToUpdateAgentClaims(false);
        }
    }, [selectedAgentValue, isNeedToUpdateAgentClaims]);

    const handleSuccessOnboardState = (response: any) => {
        const onboardState = response.data.value;

        setInOnboardState(onboardState);
    };

    const getOtpToken = () => {
        let mfaEnabledAttr: any = null;

        thisUser.Attributes.forEach(function (a) {
            if (a.Key === otpEnabledAttributeName) {
                mfaEnabledAttr = a;
            }
        });

        return mfaEnabledAttr;
    };

    const hasOtpToken = () => {
        if (!thisUser || !thisUser.Attributes) {
            return false;
        }

        let mfaEnabledAttr = getOtpToken();

        return !!mfaEnabledAttr;
    };

    const otpCreated = () => {
        if (!thisUser || !thisUser.Attributes) {
            return false;
        }

        let mfaEnabledAttr = getOtpToken();

        if (!mfaEnabledAttr) {
            return null;
        }

        return mfaEnabledAttr.Created;
    };

    const hasPushToken = () => {
        return thisUser.IdentityAuthenticators && thisUser.IdentityAuthenticators.length > 0;
    };

    const getU2fToken = () => {
        let u2fEnabledAttr: any = null;

        thisUser.Attributes.forEach(function (a) {
            if (a.Key === u2fEnabledAttributeName) {
                u2fEnabledAttr = a;
            }
        });

        return u2fEnabledAttr;
    };

    const hasU2fToken = () => {
        if (!thisUser || !thisUser.Attributes) {
            return false;
        }

        let u2fEnabledAttr = getU2fToken();

        return !!u2fEnabledAttr;
    };

    const u2fCreated = () => {
        if (!thisUser || !thisUser.Attributes) {
            return false;
        }

        let u2fEnabledAttr = getU2fToken();

        if (!u2fEnabledAttr) {
            return null;
        }

        return u2fEnabledAttr.Created;
    };

    const resolveOrgName = (item: any) => {
        if (item && item.OrganizationId) {
            const orgs: any = shellConfig.organizations;

            for (let org of orgs) {
                if (org.id === item.OrganizationId) {
                    return org.name;
                }
            }
        }

        return null;
    };

    const translateAttributeName = (attributeKey: string) => {
        const name: TranslationsTypes = createTranslation('KNOWN_ATTRIBUTE_' + attributeKey.replace('.', '_').toUpperCase());

        const translated: string = translation(name);

        if (translated === name) {
            return attributeKey;
        }

        return translated;
    };

    const fromFileTime = (fileTime: number) => {
        return new Date(fileTime / 10000 - 11644473600000);
    };

    const getFullTimestamp = (dt: Date) => {
        return passlyFormatDate(dt, DATE_TIME_FORMAT);
    };

    const prettifyAttributeValue = (name: string, value: any) => {
        let dt: Date;
        let year: number;

        switch (name) {
            case 'PwdLastSet':
                dt = fromFileTime(value);

                year = dt.getFullYear();

                if (year < 1970) {
                    return translation('DATE_NEVER');
                }

                return getFullTimestamp(dt);
            case 'LastSynchronized':
                year = +value.substr(0, 4);

                if (year < 1970) {
                    return translation('DATE_NEVER');
                }

                return getFullTimestamp(value);
            case 'AccountExpires':
                dt = fromFileTime(value);

                year = dt.getFullYear();

                if (year > new Date().getFullYear() + 100) {
                    return translation('DATE_NEVER');
                }

                return getFullTimestamp(dt);
            case 'Active':
                return value ? 'true' : 'false';
            case 'passwordLastChanged':
            case 'mfaLastUsed':
            case 'otpEnabled':
                return getFullTimestamp(value);
            default:
                return value;
        }
    };

    const hasValue = (value: any) => {
        return value !== null;
    };

    const resetUserPassword = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('CHANGE_PASSWORD_HEADER'),
            modalSwitcher: modalSwitchState,
            form: <ResetPasswordDialog userId={userId} userPasswordPolicy={userPasswordPolicy} />
        });
    };

    const addEditUserAttribute = (formMode: FormMode, attributeId: string, attributeName: string, attributeValue: string) => {
        dispatch(getModalSwitch(true));

        if (formMode === FormMode.Add) {
            setModalData({
                headerTitle: translation('ATTRIBUTE_ADD'),
                modalSwitcher: modalSwitchState,
                form: <AddEditUserAttributesDialog userId={userId} attributeId='' attributeName='' attributeValue='' formMode={formMode} />
            });
        } else if (formMode === FormMode.Edit) {
            setModalData({
                headerTitle: translation('ATTRIBUTE_EDIT'),
                modalSwitcher: modalSwitchState,
                form: (
                    <AddEditUserAttributesDialog
                        userId={userId}
                        attributeId={attributeId}
                        attributeName={attributeName}
                        attributeValue={attributeValue}
                        formMode={formMode}
                    />
                )
            });
        }
    };

    const deleteUserAttribute = (attributeId: string) => {
        confirm(translation('DELETE_ATTRIBUTE_CONFIRM'), translation('OK'), translation('CANCEL'), translation('CONFIRM_DELETE'), false, {
            isDeleteAction: true
        }).then(
            () => {
                dispatch(handleDeleteUserAttribute(userId, attributeId, genericErrorToast));
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const handleExpirePassword = () => {
        confirm(translation('EXPIRE_PASSWORD_BODY'), translation('OK'), translation('CANCEL'), translation('EXPIRE_PASSWORD_HEADER')).then(
            () => {
                dispatch(handleExpireUserPassword(userId));
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const handleRemoveRole = (roleName: string, roleId: string) => {
        const roleBody: string = replaceDynamicTranslation(translation('CONFIRM_REMOVE_ROLE_BODY'), roleName);

        confirm(roleBody, translation('REMOVE_BUTTON'), translation('CANCEL'), translation('CONFIRM_REMOVE_ROLE'), false, {
            isDeleteAction: true
        }).then(
            () => {
                setRemoveRoleId(roleId);
                dispatch(handleRemoveRoleMembership(roleId, userId));
            },
            () => {
                // on cancel
                setRemoveRoleId('');
            }
        );
    };

    const handleRemoveGroup = (groupName: string, groupId: string) => {
        const groupBody: string = replaceDynamicTranslation(translation('CONFIRM_REMOVE_GROUP_BODY'), groupName);

        confirm(groupBody, translation('REMOVE_BUTTON'), translation('CANCEL'), translation('CONFIRM_REMOVE_GROUP'), false, {
            isDeleteAction: true
        }).then(
            () => {
                setRemoveGroupId(groupId);
                dispatch(handleRemoveGroupMembership(groupId, userId));
            },
            () => {
                // on cancel
                setRemoveGroupId('');
            }
        );
    };

    const revokePush = (pushTokenId: string) => {
        confirm(
            translation('REVOKE_PUSH_TOKEN_BODY'),
            translation('REVOKE_BUTTON'),
            translation('CANCEL'),
            translation('REVOKE_PUSH_TOKEN_HEADER')
        ).then(
            () => {
                dispatch(handleRevokePush(userId, pushTokenId));
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const revokeU2f = () => {
        confirm(
            translation('REVOKE_U2F_TOKEN_BODY'),
            translation('REVOKE_BUTTON'),
            translation('CANCEL'),
            translation('REVOKE_U2F_TOKEN_HEADER')
        ).then(
            () => {
                dispatch(handleRevokeU2f(userId));
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const revokeOtp = () => {
        confirm(
            translation('REVOKE_OTP_TOKEN_BODY'),
            translation('REVOKE_BUTTON'),
            translation('CANCEL'),
            translation('REVOKE_OTP_TOKEN_HEADER')
        ).then(
            () => {
                dispatch(handleRevokeOtp(userId));
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const editUser = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('EDIT_USER'),
            modalSwitcher: modalSwitchState,
            form: <EditUserDialog person={thisUser} editUserCompleteCallback={onEditUserComplete} isDirSync={dirSync} />
        });
    };

    const onEditUserComplete = (shouldReloadUser: boolean) => {
        dispatch(getModalSwitch(false));
        if (shouldReloadUser) {
            dispatch(handleGetUserData(userId));
        }
    };

    const addRolesModal = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('ADD_ROLES'),
            modalSwitcher: modalSwitchState,
            form: <AddRolesDialog />
        });
    };

    const handleAddImpersonationGroups = (userId: string, groups: any[]): void => {
        const success = () => {
            handleModalSuccess();
            setTriggerJit(true);
        };

        usersService.addImpersonationGroups(
            userId,
            groups.map((group: IGroupsDialogData) => group.id),
            success,
            genericErrorToast
        );
    };

    const addGroupsModal = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('ADD_GROUPS'),
            modalSwitcher: modalSwitchState,
            form: (
                <AddGroups
                    dataFunction={usersService.getNonMemberedGroups}
                    requestFunction={handleAddGroupsMembership}
                    noDataLabel={'NO_MEMBER_GROUPS_TO_ADD'}
                />
            )
        });
    };

    const addGroupsToJit = (): void => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('ADD_GROUPS'),
            modalSwitcher: modalSwitchState,
            form: (
                <AddGroups
                    dataFunction={usersService.getImpersonationGroupsNotMembered}
                    requestFunction={handleAddImpersonationGroups}
                    noDataLabel={'NO_GROUPS_IN_GROUP'}
                    noDispatch={true}
                />
            )
        });
    };

    const handleDesyncModal = (): void => {
        const handleDesyncSuccess = (): void => {
            isDirSync(false);
            dispatch(handleGetUserData(userId));
        };

        usersService.desyncUser(userId, handleDesyncSuccess, genericErrorToast);
    };

    const desyncUser = () => {
        confirm(translation('DESYNC_USER_CONFIRM_BODY'), translation('DESYNC_USER'), translation('CANCEL'), translation('DESYNC_USER_CONFIRM')).then(
            () => {
                handleDesyncModal();
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const sendOnboardEmail = () => {
        usersService.sendOnboardEmail(userId, genericErrorToast, handleSuccessSendOnboardEmail);
    };

    const handleSuccessSendOnboardEmail = () => {
        const args: string[] = [thisUser.DisplayName];
        const message = interpolateTranslation(translation('SUCCESS_SENT_ONBOARD_EMAIL'), args);

        toast(message);
    };

    const unlockAccount = () => {
        usersService
            .unlockUser(thisUser.Id)
            .then(() => {
                onEditUserComplete(true);
            })
            .catch((error: AxiosError<any>) => {
                genericErrorToast(error);
            });
    };

    const editCustomClaims = () => {
        if (thisUser) {
            dispatch(getModalSwitch(true));

            setModalData({
                headerTitle: translation('EDIT_USER'),
                modalSwitcher: modalSwitchState,
                form: (
                    <EditCustomClaimsDialog
                        user={thisUser}
                        selectedAgent={selectedAgentValue}
                        agentClaims={agentClaims}
                        setIsNeedToUpdateAgentClaims={setIsNeedToUpdateAgentClaims}
                    />
                )
            });
        }
    };

    const forceSignoutButtonClick = () => {
        confirm(translation('FORCE_SIGNOUT_USER_CONFIRM'), translation('SIGNOUT'), translation('NO'), translation('CONFIRM'), false, {
            isDeleteAction: true
        }).then(
            () => {
                usersService.forceSignout(userId)
                    .then(() => {
                        dispatch(handleSetSnackbar({ show: true, message: translation('USER_FORCE_SIGNED_OUT'), severity: 'success' }));
                    })
                    .catch(() => {
                        dispatch(handleSetSnackbar({ show: true, message: translation('GENERIC_ERROR'), severity: 'error' }))
                    });
            },
            () => {
                // on cancel
            }
        );
    };

    return (
        <>
            {loadingData && <PageLoader />}
            {!loadingData && (
                <>
                    <ModalComponent {...modalData} />
                    <div className='content--narrow detailPage'>
                        <div className='detailPage__header'>
                            <div className='detailPage__header-cover'>
                                <div className='detailPage__header-detail'>
                                    <div className='detailPage__header-detail-content'>
                                        <AvatarComponent userId={thisUser.Id} size='large' />
                                    </div>
                                </div>
                                <div className='detailPage__profile-large-title'>
                                    <h1 className='detailPage__display-name'>{thisUser.DisplayName}</h1>
                                    <h3 className='detailPage__principal-name'>
                                        <span className='detailPage__principal-name-icon d__inline__block v_align__m'>
                                            <PersonIcon />
                                        </span>
                                        <span className='detailPage__principal-name-icon d__inline__block v_align__m'>{thisUser.PrincipalName}</span>
                                    </h3>
                                    <h5 className='detailPage__principal-name-h5'>
                                        <span className='detailPage__principal-name-icon d__inline__block v_align__m'>
                                            <EmailIcon />
                                        </span>
                                        <span>{thisUser.EmailAddress}</span>
                                    </h5>
                                </div>
                            </div>
                            {dirSync && hasOrgPermission([ManageAccount]) && (
                                <div className='detailPage__header-actions'>
                                    <PasslyButton variant='contained' color='primary' onClick={desyncUser}>
                                        {translation('DESYNC_USER')}
                                    </PasslyButton>
                                </div>
                            )}
                        </div>
                        <div className='detailPage__header-specifics'>
                            <div className='detailPage__specifics-container'>
                                <div className='detailPage__specific'>
                                    <div className='detailPage__specific-detail'>
                                        {thisUser.LastSignedIn ? (
                                            <span>{passlyFormatDate(thisUser.LastSignedIn, DATE_FORMAT)}</span>
                                        ) : (
                                            <span>{translation('NEVER_LOGGED_ON')}</span>
                                        )}
                                    </div>
                                    <span className='detailPage__specific-detail-label'>{translation('USERS_LAST_LOGON')}</span>
                                </div>
                                <div className='detailPage__specific'>
                                    <div className='detailPage__specific-detail'>{numberOfDevices(thisUser.Grants)}</div>
                                    <span className='detailPage__specific-detail-label'>{translation('TRUSTED_DEVICES_SECTION')}</span>
                                </div>
                                {hasDefaultOrgAdminPermission() && isDefaultOrgUser() && isLoggedInToDefaultOrg() && (
                                    <div className='detailPage__specific'>
                                        <div className='detailPage__specific-detail'>
                                        <PasslyButton
                                                id='signoutUser'
                                                variant='contained'
                                                color='primary'
                                                onClick={() => forceSignoutButtonClick()}>
                                                    {translation('SIGNOUT')}
                                            </PasslyButton>
                                        </div>
                                        <span className='detailPage__specific-detail-label'>{translation('USER_FORCE_SIGNOUT_LABEL')}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className='detailPage__section'>
                            <div className='detailPage__section-head'>{translation('ACCOUNT_DETAILS')}</div>
                            {dirSync && <Infobox text={'DIR_SYNC_BLOCK'} icon={<InfoOutlined />} />}
                            {office365 && <Infobox text={'OFFICE_365_SYNC'} icon={<InfoOutlined />} />}
                            <div className='detailPage__section-content'>
                                <ul className='detailPage__list'>
                                    {hasOrgPermission([ManageAccount]) && (
                                        <li>
                                            <a id='editUserLink' className='detailPage__list--primary-label' onClick={() => editUser()}>
                                                {translation('EDIT')}
                                            </a>
                                        </li>
                                    )}
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('USER_FULL_NAME')}</span>
                                        <span className='detailPage__list--secondary-label'>{thisUser.DisplayName}</span>
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('EMAIL_ADDR')}</span>
                                        <span className='detailPage__list--secondary-label'>{thisUser.EmailAddress}</span>
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('USERNAME')}</span>
                                        <span className='detailPage__list--secondary-label'>{thisUser.PrincipalName}</span>
                                    </li>
                                    {thisUser.AlternatePrincipalNames && thisUser.AlternatePrincipalNames.length > 0 && (
                                        <li>
                                            <span className='detailPage__list--primary-label'>{translation('APN_TITLE')}</span>
                                            <span className='detailPage__list--secondary-label'>
                                                {thisUser.AlternatePrincipalNames.map((item: IAlternatePrincipalName, index: number) => (
                                                    <span className='detailPage__list--secondary-label-child' key={index}>
                                                        {item.PrincipalName}
                                                    </span>
                                                ))}
                                            </span>
                                        </li>
                                    )}
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('USERS_STATE')}</span>
                                        <span className='detailPage__list--secondary-label'>
                                            {/*TODO: it should be fixed on the backend one day*/}
                                            {thisUser.ProvisioningState === 'Disabled'
                                                ? translation('PROVISION_STATE_INACTIVE')
                                                : thisUser.ProvisioningState}
                                        </span>
                                    </li>
                                    {inOnboardState && (
                                        <li>
                                            <span className='detailPage__list--primary-label'>{translation('ONBOARDING')}</span>
                                            <span className='detailPage__list--secondary-label'>
                                                <Link onClick={() => sendOnboardEmail()}>{translation('SEND_EMAIL')}</Link>
                                            </span>
                                        </li>
                                    )}
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('USERS_LAST_LOGON')}</span>
                                        <span className='detailPage__list--secondary-label'>
                                            {thisUser.LastSignedIn ? (
                                                <span>{passlyFormatDate(thisUser.LastSignedIn, DATE_TIME_FORMAT)}</span>
                                            ) : (
                                                <span>{translation('DATE_NEVER')}</span>
                                            )}
                                        </span>
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('USER_LAST_PASSWORD_CHANGE')}</span>
                                        <span className='detailPage__list--secondary-label'>
                                            {thisUser.IsPasswordExpired ? `${translation('EXPIRED')} ` : ''}
                                            {!thisUser.IsPasswordExpired ? (
                                                thisUser.PasswordLastChangedResetAfterExpired ? (
                                                    <span>{passlyFormatDate(thisUser.PasswordLastChangedResetAfterExpired, DATE_TIME_FORMAT)}</span>
                                                ) : (
                                                    <span>{translation('DATE_NEVER')}</span>
                                                )
                                            ) : null}
                                            {thisUser.IsPasswordExpired ? (
                                                thisUser.PasswordLastChangedNoReset ? (
                                                    <span>{passlyFormatDate(thisUser.PasswordLastChangedNoReset, DATE_TIME_FORMAT)}</span>
                                                ) : (
                                                    <span>{translation('DATE_NEVER')}</span>
                                                )
                                            ) : null}
                                            <span>
                                                {/*canChangePass (local/dirsync 2-way) && */}{' '}
                                                {hasOrgPermission([ManageAccount]) &&
                                                    userPasswordPolicy.CanChangePassword &&
                                                    userPasswordPolicy.ChangePasswordEnabled && (
                                                        <Fragment>
                                                            (
                                                            <a id='resetUserPasswordLink' onClick={() => resetUserPassword()}>
                                                                {translation('DIR_RESET_PASSWORD')}
                                                            </a>
                                                            {!thisUser.IsPasswordExpired && ' | '}
                                                            {!thisUser.IsPasswordExpired && (
                                                                <a id='expireUserPasswordLink' onClick={() => handleExpirePassword()}>
                                                                    {translation('DIR_EXPIRE_PASSWORD')}
                                                                </a>
                                                            )}
                                                            )
                                                        </Fragment>
                                                    )}
                                            </span>
                                        </span>
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('USER_ACCOUNT_LOCKOUT_STATE')}</span>
                                        <span className='detailPage__list--secondary-label'>
                                            {thisUser.IsLockedOut ? (
                                                <span>{translation('USER_ACCOUNT_LOCKED')}</span>
                                            ) : (
                                                <span>{translation('USER_ACCOUNT_UNLOCKED')}</span>
                                            )}
                                        </span>
                                    </li>
                                    {thisUser.IsLockedOut && (
                                        <li>
                                            <span className='detailPage__list--primary-label'>{translation('USER_ACCOUNT_LOCKED_UNTIL')}</span>
                                            <span className='detailPage__list--secondary-label'>
                                                {passlyFormatDate(thisUser.LockedOutUntil, DATE_TIME_FORMAT)}
                                                {hasOrgPermission([ManageAccount]) && (
                                                    <span>
                                                        &nbsp;(<a onClick={() => unlockAccount()}>{translation('USER_ACCOUNT_UNLOCK')}</a>)
                                                    </span>
                                                )}
                                            </span>
                                        </li>
                                    )}
                                    <li>
                                        <span className='detailPage__indented-list--label'>
                                            <JitToggle checked={isJitDisabled} />
                                        </span>
                                    </li>

                                    {hasOrgPermission([ManageAccount]) && (
                                        <li>
                                            <span className='detailPage__list--primary-label' />
                                            <span className='detailPage__list--secondary-label'>
                                                <PasslyTooltip title={hasPushToken() || hasOtpToken() ? '' : translation('OTP_NOT_REGISTERED')}>
                                                    <span>
                                                        <PasslyButton
                                                            id='verifyUser'
                                                            disabled={!hasPushToken() && !hasOtpToken()}
                                                            variant='contained'
                                                            color='primary'
                                                            onClick={() => basicService.redirect(window.location.pathname + '/verifyuser')}
                                                        >
                                                            {translation('VERIFY_USER')}
                                                        </PasslyButton>
                                                    </span>
                                                </PasslyTooltip>
                                            </span>
                                        </li>
                                    )}
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('PUSH_TOKEN_LABEL')}</span>
                                        {hasPushToken() ? (
                                            <span>
                                                {hasOrgPermission([ManageAccount]) && (
                                                    <a id='revokePushTokenLink' onClick={() => revokePush(thisUser.IdentityAuthenticators[0].Id)}>
                                                        {translation('REVOKE_TOKEN')}
                                                    </a>
                                                )}
                                                <ul className='detailPage__list'>
                                                    <li>
                                                        <span className='detailPage__list--primary-label-bold'>
                                                            {translation('PUSH_TOKEN_TYPE')}&nbsp;
                                                        </span>
                                                        <span className='detailPage__list--secondary-label'>
                                                            {thisUser.IdentityAuthenticators[0].Device}{' '}
                                                            {thisUser.IdentityAuthenticators[0].OperatingSystem}
                                                        </span>
                                                    </li>
                                                    <li>
                                                        <span className='detailPage__list--primary-label-bold'>
                                                            {translation('PUSH_TOKEN_VERSION')}&nbsp;
                                                        </span>
                                                        <span className='detailPage__list--secondary-label'>
                                                            {thisUser.IdentityAuthenticators[0].AuthenticatorVersion}
                                                        </span>
                                                    </li>
                                                    <li>
                                                        <span className='detailPage__list--primary-label-bold'>
                                                            {translation('PUSH_TOKEN_PROVISION_STATE')}&nbsp;
                                                        </span>
                                                        <span className='detailPage__list--secondary-label'>
                                                            {thisUser.IdentityAuthenticators[0].ProvisioningState}
                                                        </span>
                                                    </li>
                                                    <li>
                                                        <span className='detailPage__list--primary-label-bold'>
                                                            {translation('TOKEN_REGISTERED')}&nbsp;
                                                        </span>
                                                        <span className='detailPage__list--secondary-label'>
                                                            {passlyFormatDate(thisUser.IdentityAuthenticators[0].Created, DATE_TIME_FORMAT)}
                                                        </span>
                                                    </li>
                                                </ul>
                                            </span>
                                        ) : (
                                            <span className='detailPage__list--secondary-label'>{translation('PUSH_NOT_REGISTERED')}</span>
                                        )}
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('OTP_TOKEN_LABEL')}</span>
                                        {hasOtpToken() ? (
                                            <span>
                                                {hasOrgPermission([ManageAccount]) && (
                                                    <a id='revokeOtpTokenLink' onClick={() => revokeOtp()}>
                                                        {translation('REVOKE_TOKEN')}
                                                    </a>
                                                )}
                                                <ul className='detailPage__list'>
                                                    <li>
                                                        <span className='detailPage__list--primary-label-bold'>
                                                            {translation('TOKEN_REGISTERED')}&nbsp;
                                                        </span>
                                                        <span className='detailPage__list--secondary-label'>
                                                            {passlyFormatDate(otpCreated(), DATE_TIME_FORMAT)}
                                                        </span>
                                                    </li>
                                                </ul>
                                            </span>
                                        ) : (
                                            <span className='detailPage__list--secondary-label'>{translation('OTP_NOT_REGISTERED')}</span>
                                        )}
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('U2F_TOKEN_LABEL')}</span>
                                        {hasU2fToken() ? (
                                            <span>
                                                {hasOrgPermission([ManageAccount]) && (
                                                    <a id='revokeU2fTokenLink' onClick={() => revokeU2f()}>
                                                        {translation('REVOKE_TOKEN')}
                                                    </a>
                                                )}
                                                <ul className='detailPage__list'>
                                                    <li>
                                                        <span className='detailPage__list--primary-label-bold'>
                                                            {translation('TOKEN_REGISTERED')}&nbsp;
                                                        </span>
                                                        <span className='detailPage__list--secondary-label'>
                                                            {passlyFormatDate(u2fCreated(), DATE_TIME_FORMAT)}
                                                        </span>
                                                    </li>
                                                </ul>
                                            </span>
                                        ) : (
                                            <span className='detailPage__list--secondary-label'>{translation('U2F_NOT_REGISTERED')}</span>
                                        )}
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className='detailPage__section'>
                            <div className='detailPage__section-head'>{translation('MEMBERSHIP_PANEL')}</div>

                            <div className='detailPage__section-content'>
                                <ul className='detailPage__list'>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('ROLES')}</span>
                                        {thisUser.MemberOf && thisUser.MemberOf.length > 0 ? (
                                            <span className='detailPage__list--secondary-label'>
                                                <ul className='detailPage__secondary-list'>
                                                    {thisUser.MemberOf.map((item: IRole, index: number) => (
                                                        <li key={index}>
                                                            <span>{item.Name}</span>
                                                            <span className='detailPage__item-highlight'>&nbsp;({resolveOrgName(item)})&nbsp;</span>
                                                            {hasOrgPermission([Administrator]) &&
                                                                item.Name.toUpperCase() !== 'USER' &&
                                                                !(
                                                                    item.OrganizationId === defaultOrgId &&
                                                                    item.Name.toUpperCase() === 'ADMINISTRATOR' &&
                                                                    thisUser.IsAccountOfRecord
                                                                ) && (
                                                                    <span>
                                                                        (
                                                                        <a
                                                                            id='removeRolesFromUserLink'
                                                                            onClick={() => handleRemoveRole(item.Name, item.Id)}
                                                                        >
                                                                            {translation('REMOVE')}
                                                                        </a>
                                                                        )
                                                                    </span>
                                                                )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </span>
                                        ) : (
                                            <span>{translation('NO_ROLES')}</span>
                                        )}
                                    </li>
                                    {hasOrgPermission([Administrator]) && (
                                        <div className='detailPage__button_container'>
                                            <PasslyButton id='addRolesToUserButton' variant='contained' color='primary' onClick={addRolesModal}>
                                                {translation('ADD_ROLES')}
                                            </PasslyButton>
                                        </div>
                                    )}

                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('GROUPS')}</span>
                                        {thisUser.Groups && thisUser.Groups.length > 0 ? (
                                            <span className='detailPage__list--secondary-label'>
                                                <ul className='detailPage__secondary-list'>
                                                    {thisUser.Groups.map((item: IGroup, index: number) => (
                                                        <li key={index}>
                                                            <span>{item.Name}</span>
                                                            <span className='detailPage__item-highlight'>&nbsp;({resolveOrgName(item)})&nbsp;</span>
                                                            {hasOrgPermission([ManageAccount]) && item.SyncSource === defaultOrgId && (
                                                                <span>
                                                                    (
                                                                    <a
                                                                        id='removeGroupsFromUserLink'
                                                                        onClick={() => handleRemoveGroup(item.Name, item.Id)}
                                                                    >
                                                                        {translation('REMOVE')}
                                                                    </a>
                                                                    )
                                                                </span>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </span>
                                        ) : (
                                            <span>{translation('NO_GROUPS')}</span>
                                        )}
                                    </li>
                                    {hasOrgPermission([ManageAccount]) && (
                                        <div className='detailPage__button_container'>
                                            <PasslyButton id='addGroupsToUserButton' variant='contained' color='primary' onClick={addGroupsModal}>
                                                {translation('ADD_GROUPS')}
                                            </PasslyButton>
                                        </div>
                                    )}
                                </ul>
                            </div>
                        </div>

                        {hasOrgPermission([ManageAccount]) && (
                            <JitSection handleAddGroups={addGroupsToJit} trigger={triggerJit} enabled={jitDisabled} />
                        )}

                        {agents?.length ? (
                            <CustomClaimsConfig
                                agentClaims={agentClaims}
                                editUserCallback={editCustomClaims}
                                agents={agents}
                                selectedAgentValue={selectedAgentValue}
                                setSelectedAgentValue={setSelectedAgentValue}
                                userId={thisUser.Id}
                            />
                        ) : null}
                        <TrustedDevices grantsInput={thisUser.Grants} isMyProfile={false} userId={userId} />

                        <div className='detailPage__section'>
                            <div className='detailPage__section-head'>{translation('ACCOUNT_ATTRIBUTES')}</div>
                            {!thisUser.Attributes || thisUser.Attributes.length === 0 ? (
                                <div className='detailPage__section-content'>
                                    <p>{translation('EMPTY_USER_ATTR_LIST')}</p>
                                </div>
                            ) : (
                                <div className='detailPage__section-content'>
                                    <ul className='detailPage__list'>
                                        <li>
                                            <span className='detailPage__list--primary-label'>{translation('ATTRIBUTE_NAME')}</span>
                                            <span className='detailPage__list--secondary-label'>{translation('ATTRIBUTE_VALUE')}</span>
                                        </li>
                                        {thisUser.Attributes.map((item: IUserAttribute, index: number) => (
                                            <div key={index}>
                                                {!userAttributesToHide.includes(item.Key) && (
                                                    <li>
                                                        <span className='detailPage__list--primary-label'>{translateAttributeName(item.Key)}</span>
                                                        <span className='detailPage__list--secondary-label'>
                                                            {item.Value}&nbsp;&nbsp;
                                                            {hasOrgPermission([ManageAccount]) && (
                                                                <span>
                                                                    (
                                                                    <a
                                                                        id='editAttributeLink'
                                                                        onClick={() =>
                                                                            addEditUserAttribute(FormMode.Edit, item.Id, item.Key, item.Value)
                                                                        }
                                                                    >
                                                                        {translation('EDIT')}
                                                                    </a>{' '}
                                                                    |
                                                                    <a id='removeAttributeLink' onClick={() => deleteUserAttribute(item.Id)}>
                                                                        {translation('REMOVE')}
                                                                    </a>
                                                                    )
                                                                </span>
                                                            )}
                                                        </span>
                                                    </li>
                                                )}
                                            </div>
                                        ))}
                                        {hasOrgPermission([ManageAccount]) && (
                                            <div className='detailPage__button_container'>
                                                <PasslyButton
                                                    variant='contained'
                                                    color='primary'
                                                    onClick={() => addEditUserAttribute(FormMode.Add, '', '', '')}
                                                >
                                                    {translation('ADD_ATTRIBUTE')}
                                                </PasslyButton>
                                            </div>
                                        )}
                                    </ul>
                                </div>
                            )}
                        </div>

                        <div className='detailPage__section'>
                            <div className='detailPage__section-head'>{translation('ACCOUNT_SYNCDATA')}</div>
                            {!thisUser.SyncData ? (
                                <div className='detailPage__section-content'>
                                    <p>{translation('EMPTY_USER_SYNCDATA')}</p>
                                </div>
                            ) : (
                                <div className='detailPage__section-content'>
                                    <ul className='detailPage__list'>
                                        <li>
                                            <span className='detailPage__list--primary-label'>{translation('ATTRIBUTE_SYNCDATA_ELEMENT')}</span>
                                            <span className='detailPage__list--secondary-label'>{translation('ATTRIBUTE_SYNCDATA_VALUE')}</span>
                                        </li>
                                        <li>
                                            <span className='detailPage__list--primary-label'>
                                                {translation('KNOWN_ATTRIBUTE_AD_ACCOUNTEXPIRES')}
                                            </span>
                                            <span className='detailPage__list--secondary-label'>
                                                {prettifyAttributeValue('AccountExpires', thisUser.SyncData.AccountExpires)}
                                            </span>
                                        </li>
                                        {hasValue(thisUser.SyncData.Active) && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>{translation('ACTIVE')}</span>
                                                <span className='detailPage__list--secondary-label'>
                                                    {prettifyAttributeValue('Active', thisUser.SyncData.Active)}
                                                </span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.City && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>{translation('CITY')}</span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.City}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.CostCenter && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>{translation('KNOWN_ATTRIBUTE_COSTCENTER')}</span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.CostCenter}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.CountryCode && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>{translation('KNOWN_ATTRIBUTE_COUNTRYCODE')}</span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.CountryCode}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.DBCSPwd && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>{translation('KNOWN_ATTRIBUTE_AD_DBCSPWD')}</span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.DBCSPwd}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.Department && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>{translation('KNOWN_ATTRIBUTE_DEPARTMENT')}</span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.Department}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.DN && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>{translation('KNOWN_ATTRIBUTE_AD_DN')}</span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.DN}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.EmployeeId && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>{translation('KNOWN_ATTRIBUTE_EMPLOYEEID')}</span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.EmployeeId}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.EmployeeNumber && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>
                                                    {translation('KNOWN_ATTRIBUTE_EMPLOYEENUMBER')}
                                                </span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.EmployeeNumber}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.LastSynchronized && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>
                                                    {translation('KNOWN_ATTRIBUTE_LASTSYNCHRONIZED')}
                                                </span>
                                                <span className='detailPage__list--secondary-label'>
                                                    {prettifyAttributeValue('LastSynchronized', thisUser.SyncData.LastSynchronized)}
                                                </span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.LmPwdHistory && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>
                                                    {translation('KNOWN_ATTRIBUTE_AD_LMPWDHISTORY')}
                                                </span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.LmPwdHistory}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.LockoutDuration && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>
                                                    {translation('KNOWN_ATTRIBUTE_LOCKOUTDURATION')}
                                                </span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.LockoutDuration}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.LockoutThreshold && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>
                                                    {translation('KNOWN_ATTRIBUTE_LOCKOUTTHRESHOLD')}
                                                </span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.LockoutThreshold}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.LockoutTime && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>{translation('KNOWN_ATTRIBUTE_LOCKOUTTIME')}</span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.LockoutTime}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.Manager && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>{translation('KNOWN_ATTRIBUTE_MANAGER')}</span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.Manager}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.Mobile && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>{translation('KNOWN_ATTRIBUTE_AD_MOBILE')}</span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.Mobile}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.NTPwdHistory && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>
                                                    {translation('KNOWN_ATTRIBUTE_AD_NTPWDHISTORY')}
                                                </span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.NTPwdHistory}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.ObjectGUID && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>
                                                    {translation('KNOWN_ATTRIBUTE_AD_OBJECTGUID')}
                                                </span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.ObjectGUID}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.ObjectSID && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>{translation('KNOWN_ATTRIBUTE_AD_OBJECTSID')}</span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.ObjectSID}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.OfficeName && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>{translation('KNOWN_ATTRIBUTE_OFFICENAME')}</span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.OfficeName}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.PostalCode && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>{translation('KNOWN_ATTRIBUTE_POSTALCODE')}</span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.PostalCode}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.PrimaryGroupId && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>
                                                    {translation('KNOWN_ATTRIBUTE_AD_PRIMARYGROUPID')}
                                                </span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.PrimaryGroupId}</span>
                                            </li>
                                        )}
                                        {prettifyAttributeValue('PwdLastSet', thisUser.SyncData.PwdLastSet) !== translation('DATE_NEVER') && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>
                                                    {translation('KNOWN_ATTRIBUTE_AD_PWDLASTSET')}
                                                </span>
                                                <span className='detailPage__list--secondary-label'>
                                                    {prettifyAttributeValue('AccountExpires', thisUser.SyncData.PwdLastSet)}
                                                </span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.State && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>{translation('KNOWN_ATTRIBUTE_STATE')}</span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.State}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.StreetAddress && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>
                                                    {translation('KNOWN_ATTRIBUTE_STREETADDRESS')}
                                                </span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.StreetAddress}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.SupplementalCredentials && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>
                                                    {translation('KNOWN_ATTRIBUTE_AD_SUPPLEMENTALCREDENTIALS')}
                                                </span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.SupplementalCredentials}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.TelephoneNumber && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>
                                                    {translation('KNOWN_ATTRIBUTE_TELEPHONENUMBER')}
                                                </span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.TelephoneNumber}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.Title && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>{translation('KNOWN_ATTRIBUTE_TITLE')}</span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.Title}</span>
                                            </li>
                                        )}
                                        {thisUser.SyncData.UserAccountControl && (
                                            <li>
                                                <span className='detailPage__list--primary-label'>
                                                    {translation('KNOWN_ATTRIBUTE_AD_USERACCOUNTCONTROL')}
                                                </span>
                                                <span className='detailPage__list--secondary-label'>{thisUser.SyncData.UserAccountControl}</span>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {showDarkWeb && <DarkWebCompromises userId={userId} />}
                    </div>
                </>
            )}
        </>
    );
};

export default UserDetailsPage;
